import React from 'react';
import './OnlineUserList.less';

import {
  Modal,
  Table,
  Tag,
  Button,
  Card,
  Select,
  Space,
  List,
} from 'antd';
import {
  ReloadOutlined,
  ExclamationCircleFilled,
  UserOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  StopOutlined,
  FilterOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import Lang from '../../common/model/global/Lang';
import DateUtil from '../../common/util/DateUtil';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import HwzComponent from '../../common/component/HwzComponent';
import HwzTitle from '../widget/HwzTitle';
import { RouteComponentProps } from 'react-router-dom';
import { UserRoleMap, UserRole } from '../../common/model/user/UserRole';
import Color from '../../common/model/base/option/Color';

interface OnlineUserVO {
  id: string;
  username: string;
  role: UserRole;
  ip: string;
  deviceName: string;   // 设备名称，用于筛选
  osName: string;
  loginTime: number;
  expireTime: number;
  status: 'online' | 'expired';
  remainingSec: number;
}

interface OnlineUserListState {
  loading: boolean;
  data: OnlineUserVO[];
  total: number;
  deviceTypeFilter: string;          // 选中的设备名称
  deviceTypeOptions: { value: string; label: string }[];
  currentPage: number;
  pageSize: number;
  forceAutoUploadModalVisible: boolean;
  forceAutoUploadTargetId: string;
  forceAutoUploadDirs: string[];
  forceAutoUploadFileTypes: string[];
  forceAutoUploadSending: boolean;
}

class OnlineUserList extends HwzComponent<RouteComponentProps, OnlineUserListState> {
  private timer: NodeJS.Timeout | null = null;
  private optionsLoaded = false;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      loading: true,
      data: [],
      total: 0,
      deviceTypeFilter: 'all',
      deviceTypeOptions: [{ value: 'all', label: Lang.t('common.all') || '全部' }],
      currentPage: 1,
      pageSize: 10,
      forceAutoUploadModalVisible: false,
      forceAutoUploadTargetId: '',
      forceAutoUploadDirs: [],
      forceAutoUploadFileTypes: [],
      forceAutoUploadSending: false,
    };
  }

  componentDidMount() {
    this.loadDeviceTypeOptions().then(() => {
      this.loadOnlineUsers();
    });
    this.timer = setInterval(() => {
      this.loadOnlineUsers();
    }, 30000);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  // 获取设备名称下拉选项（基于所有在线用户的 device_name 去重）
  private loadDeviceTypeOptions = async () => {
    if (this.optionsLoaded) return;
    this.setState({ loading: true });
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('pageSize', '2000'); // 一次性拉取所有在线用户（仅用于提取设备名称）
      const response = await fetch('/api/user/online/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: params.toString(),
      });
      const resp = await response.json();
      if (resp.code === 'OK' || resp.code === 'SUCCESS') {
        const records = resp.data?.data || [];
        const deviceNameSet = new Set<string>();
        records.forEach((item: any) => {
          if (item.device_name) deviceNameSet.add(item.device_name);
        });
        const options = Array.from(deviceNameSet)
          .sort()
          .map((name) => ({ value: name, label: name }));
        this.setState({
          deviceTypeOptions: [{ value: 'all', label: Lang.t('common.all') || '全部' }, ...options],
        });
        this.optionsLoaded = true;
      }
    } catch (error) {
      console.error('加载设备类型选项失败:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  // 加载在线用户列表（分页+按设备名称筛选）
  private loadOnlineUsers = () => {
    const { currentPage, pageSize, deviceTypeFilter } = this.state;
    this.setState({ loading: true });

    const params = new URLSearchParams();
    params.append('page', currentPage.toString());
    params.append('pageSize', pageSize.toString());
    if (deviceTypeFilter !== 'all') {
      params.append('deviceType', deviceTypeFilter);
    }

    fetch('/api/user/online/list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      credentials: 'include',
      body: params.toString(),
    })
      .then((res) => res.json())
      .then((resp: any) => {
        if (resp.code === 'OK' || resp.code === 'SUCCESS') {
          const pageResult = resp.data;
          const records = pageResult.data || [];
          const userList: OnlineUserVO[] = records.map((item: any) => ({
            id: item.user_uuid,
            username: item.username,
            role: item.role,
            ip: item.ip,
            deviceName: item.device_name,
            osName: item.os_info,
            loginTime: item.login_time ? new Date(item.login_time).getTime() : 0,
            expireTime: item.expire_time ? new Date(item.expire_time).getTime() : 0,
            status: item.remaining_sec > 0 ? 'online' : 'expired',
            remainingSec: item.remaining_sec || 0,
          }));
          this.setState({
            data: userList,
            total: pageResult.total || 0,
            loading: false,
          });
        } else {
          MessageBoxUtil.error(resp.message || Lang.t('common.loadFailed'));
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        console.error('获取在线用户列表失败:', error);
        this.setState({ loading: false });
        MessageBoxUtil.error('请求失败: ' + error.message);
      });
  };

  // 检查是否为PCAPP客户端（仅看deviceName，设备信息中osName可能为Windows 10等，不能作为依据）
  private isPcAppClient(record: OnlineUserVO): boolean {
    const deviceName = (record.deviceName || '').toLowerCase();
    return deviceName.includes('pc');
  }

  // 强制自动上传点击处理
  private handleForceAutoUploadClick = (record: OnlineUserVO) => {
    if (!this.isPcAppClient(record)) {
      MessageBoxUtil.warning(Lang.t('user.onlineWebNoSupport'));
      return;
    }
    this.setState({
      forceAutoUploadModalVisible: true,
      forceAutoUploadTargetId: record.id,
      forceAutoUploadDirs: [],
      forceAutoUploadFileTypes: [],
    });
  };

  private closeForceAutoUploadModal = () => {
    this.setState({ forceAutoUploadModalVisible: false });
  };

  private addUploadDir = () => {
    const { forceAutoUploadDirs } = this.state;
    const newDir = prompt(Lang.t('preference.enterDirectoryPath'));
    if (newDir && newDir.trim()) {
      this.setState({ forceAutoUploadDirs: [...forceAutoUploadDirs, newDir.trim()] });
    }
  };

  private removeUploadDir = (index: number) => {
    const { forceAutoUploadDirs } = this.state;
    const newDirs = [...forceAutoUploadDirs];
    newDirs.splice(index, 1);
    this.setState({ forceAutoUploadDirs: newDirs });
  };

  private addFileType = () => {
    const { forceAutoUploadFileTypes } = this.state;
    const newType = prompt(Lang.t('preference.enterFileType'), '.txt');
    if (newType && newType.trim()) {
      let ext = newType.trim();
      if (!ext.startsWith('.')) ext = '.' + ext;
      if (!forceAutoUploadFileTypes.includes(ext)) {
        this.setState({ forceAutoUploadFileTypes: [...forceAutoUploadFileTypes, ext] });
      } else {
        MessageBoxUtil.warning(Lang.t('preference.fileTypeExists'));
      }
    }
  };

  private removeFileType = (index: number) => {
    const { forceAutoUploadFileTypes } = this.state;
    const newTypes = [...forceAutoUploadFileTypes];
    newTypes.splice(index, 1);
    this.setState({ forceAutoUploadFileTypes: newTypes });
  };

  private sendForceAutoUpload = () => {
    const { forceAutoUploadTargetId, forceAutoUploadDirs, forceAutoUploadFileTypes } = this.state;
    this.setState({ forceAutoUploadSending: true });
    const config = {
      userId: forceAutoUploadTargetId,
      uploadDirs: forceAutoUploadDirs,
      fileTypes: forceAutoUploadFileTypes,
    };
    fetch('/api/security-app/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(config),
    })
      .then((res) => res.json())
      .then((data) => {
        this.setState({ forceAutoUploadSending: false });
        if (data.code === 'OK' || data.code === 'SUCCESS') {
          MessageBoxUtil.success(Lang.t('common.operationSuccess'));
          this.closeForceAutoUploadModal();
        } else {
          MessageBoxUtil.error(data.message || Lang.t('common.operationFailed'));
        }
      })
      .catch((error) => {
        this.setState({ forceAutoUploadSending: false });
        MessageBoxUtil.error(Lang.t('common.operationFailed') + ': ' + error.message);
      });
  };

  // 强制下线
  private forceLogout = (userId: string) => {
    Modal.confirm({
      title: Lang.t('user.onlineforceLogout'),
      icon: <ExclamationCircleFilled twoToneColor={Color.WARNING} />,
      content: Lang.t('user.onlineconfirmLogout'),
      onOk: () => {
        fetch('/api/user/online/forceLogout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.code === 'OK') {
              MessageBoxUtil.success(Lang.t('common.operationSuccess'));
              this.loadOnlineUsers();
            } else {
              MessageBoxUtil.error(data.message || Lang.t('common.operationFailed'));
            }
          });
      },
    });
  };

  private handleDeviceTypeChange = (value: string) => {
    this.setState(
      { deviceTypeFilter: value, currentPage: 1 },
      () => this.loadOnlineUsers()
    );
  };

  private handleRefresh = () => {
    this.loadOnlineUsers();
  };

  private handleTableChange = (pagination: any) => {
    this.setState(
      {
        currentPage: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
      },
      () => this.loadOnlineUsers()
    );
  };

  render() {
    const { loading, data, total, deviceTypeFilter, deviceTypeOptions, currentPage, pageSize, forceAutoUploadModalVisible, forceAutoUploadDirs, forceAutoUploadFileTypes, forceAutoUploadSending } = this.state;

    const columns: any[] = [
      {
        title: Lang.t('user.username'),
        dataIndex: 'username',
        width: 150,
        render: (text: string) => (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {text}
          </span>
        ),
      },
      {
        title: Lang.t('user.role'),
        dataIndex: 'role',
        width: 100,
        render: (text: UserRole) => (
          <Tag color={UserRoleMap[text]?.color || 'default'}>
            {UserRoleMap[text]?.name || text}
          </Tag>
        ),
      },
      {
        title: Lang.t('user.onlinedeviceInfo'),
        dataIndex: 'ip',
        width: 220,
        render: (text: string, record: OnlineUserVO) => (
          <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
            <div>IP: {text}</div>
            <div>
              <DesktopOutlined style={{ marginRight: 4 }} />
              {record.deviceName}
            </div>
            <div style={{ color: '#999' }}>{record.osName}</div>
          </div>
        ),
      },
      {
        title: Lang.t('user.onlineloginTime'),
        dataIndex: 'loginTime',
        width: 180,
        render: (text: number) => <span>{DateUtil.simpleDateTime(new Date(text))}</span>,
      },
      {
        title: Lang.t('user.onlinestatus'),
        dataIndex: 'status',
        width: 100,
        render: (text: 'online' | 'expired') => (
          <Tag
            color={text === 'online' ? 'green' : 'red'}
            icon={text === 'online' ? <CheckCircleOutlined /> : <StopOutlined />}
          >
            {text === 'online' ? Lang.t('user.onlinestatusOnline') : Lang.t('user.onlinestatusExpired')}
          </Tag>
        ),
      },
      {
        title: Lang.t('matter.operations'),
        width: 200,
        render: (_: any, record: OnlineUserVO) => (
          <Space>
            <Button
              type="link"
              size="small"
              danger
              onClick={() => this.forceLogout(record.id)}
              style={{ padding: 0 }}
            >
              {Lang.t('user.onlineforceLogout')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => this.handleForceAutoUploadClick(record)}
              style={{ padding: 0 }}
            >
              {Lang.t('user.onlineForceAutoUpload')}
            </Button>
          </Space>
        ),
      },
    ];

    return (
      <div className="page-user-online">
        <HwzTitle name={Lang.t('layout.onlineUsers')}>
          {/* 将筛选和刷新按钮放在标题行的最右侧 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Space>
              <Select
                value={deviceTypeFilter}
                onChange={this.handleDeviceTypeChange}
                options={deviceTypeOptions}
                style={{ width: 160 }}
                placeholder={Lang.t('user.deviceTypeFilter') || '设备类型'}
                suffixIcon={<FilterOutlined />}
                allowClear={false}
              />
              <Button icon={<ReloadOutlined />} onClick={this.handleRefresh} loading={loading}>
                {Lang.t('user.onlinerefreshList')}
              </Button>
            </Space>
          </div>
        </HwzTitle>

        <Card
          style={{ marginTop: 16 }}
          extra={`${Lang.t('user.onlinecurrentOnline')}: ${total} ${Lang.t('user.onlinepeople')}`}
        >
          <Table
            loading={loading}
            dataSource={data}
            columns={columns}
            rowKey="id"
            pagination={{
              total,
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              showTotal: (t) => `${Lang.t('common.total')} ${t} ${Lang.t('user.onlinepeople')}`,
            }}
            onChange={this.handleTableChange}
          />
        </Card>

        <Modal
          title={Lang.t('user.onlineForceAutoUpload')}
          visible={forceAutoUploadModalVisible}
          onCancel={this.closeForceAutoUploadModal}
          footer={
            <Space>
              <Button onClick={this.closeForceAutoUploadModal}>
                {Lang.t('cancel')}
              </Button>
              <Button type="primary" loading={forceAutoUploadSending} onClick={this.sendForceAutoUpload}>
                {Lang.t('user.onlineSend')}
              </Button>
            </Space>
          }
          width={520}
          destroyOnClose
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span>{Lang.t('preference.uploadDirectories')}</span>
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={this.addUploadDir}>
                {Lang.t('preference.addDirectory')}
              </Button>
            </div>
            {forceAutoUploadDirs.length === 0 ? (
              <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {Lang.t('preference.noUploadDirectories')}
              </div>
            ) : (
              <List
                size="small"
                bordered
                dataSource={forceAutoUploadDirs}
                renderItem={(dir: string, idx: number) => (
                  <List.Item
                    actions={[
                      <Button type="link" danger size="small" onClick={() => this.removeUploadDir(idx)}>
                        {Lang.t('delete')}
                      </Button>
                    ]}
                  >
                    {dir}
                  </List.Item>
                )}
              />
            )}
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span>{Lang.t('preference.fileTypes')}</span>
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={this.addFileType}>
                {Lang.t('preference.addFileType')}
              </Button>
            </div>
            {forceAutoUploadFileTypes.length === 0 ? (
              <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {Lang.t('preference.noFileTypes')}
              </div>
            ) : (
              <List
                size="small"
                bordered
                dataSource={forceAutoUploadFileTypes}
                renderItem={(type: string, idx: number) => (
                  <List.Item
                    actions={[
                      <Button type="link" danger size="small" onClick={() => this.removeFileType(idx)}>
                        {Lang.t('delete')}
                      </Button>
                    ]}
                  >
                    {type}
                  </List.Item>
                )}
              />
            )}
          </div>
        </Modal>
      </div>
    );
  }
}

export default OnlineUserList;